import { useMemo, useState } from 'react';
import Button from '../ui/Button';
import FilterChip from '../ui/FilterChip';
import ArtistRow from './ArtistRow';
import ArtistVotersModal from './ArtistVotersModal';
import LineupScheduleGrid from './LineupScheduleGrid';
import LineupViewToggle from './LineupViewToggle';
import {
  festivalDaysFromLineup,
  formatDayAbbrev,
  rowSizeForArtist,
  voteKey,
} from '../../utils/lineupFormat';
import { lineupHasScheduleData } from '../../utils/scheduleGrid';
import { useMyArtistVotes } from '../../hooks/queries/useEventLineup';
import useArtistVoteToggle from '../../hooks/useArtistVoteToggle';

export default function GroupLineupVotesList({
  lineup = [],
  eventId,
  groupId,
  votersByArtist = {},
  viewerId,
  totalMemberCount,
  showBlockedFootnote = false,
}) {
  const { data: savedVotes = [] } = useMyArtistVotes(eventId, viewerId);

  const [dayFilter, setDayFilter] = useState(null);
  const [showUnvoted, setShowUnvoted] = useState(false);
  const [openArtist, setOpenArtist] = useState(null);
  const [lineupView, setLineupView] = useState('list');

  const days = useMemo(() => festivalDaysFromLineup(lineup), [lineup]);
  const scheduleDay = dayFilter ?? (days.length === 1 ? days[0] : null);
  const canShowSchedule = Boolean(scheduleDay) && lineupHasScheduleData(lineup, scheduleDay);
  const activeLineupView = canShowSchedule ? lineupView : 'list';

  const selectedKeys = useMemo(
    () => new Set(savedVotes.map((v) => voteKey(v.artist_id, v.performance_day))),
    [savedVotes],
  );

  const toggleVote = useArtistVoteToggle({ eventId, viewerId, groupId, selectedKeys });

  const scoped = useMemo(
    () => (dayFilter ? lineup.filter((row) => row.performance_day === dayFilter) : lineup),
    [lineup, dayFilter],
  );

  const { voted, unvoted } = useMemo(() => {
    const withCounts = scoped.map((row) => ({
      row,
      count: (votersByArtist[row.artist_id] || []).length,
    }));
    const votedRows = withCounts
      .filter(({ count }) => count > 0)
      .sort((a, b) => b.count - a.count)
      .map(({ row }) => row);
    const unvotedRows = withCounts.filter(({ count }) => count === 0).map(({ row }) => row);
    return { voted: votedRows, unvoted: unvotedRows };
  }, [scoped, votersByArtist]);

  if (lineup.length === 0) {
    return <p className="py-6 text-center text-sm text-ink/60">No lineup announced yet.</p>;
  }

  const renderRows = (rows, { dimmed = false } = {}) => (
    <div>
      {rows.map((row) => (
        <ArtistRow
          key={voteKey(row.artist_id, row.performance_day)}
          artist={row}
          size={rowSizeForArtist(row)}
          layout="group"
          voters={votersByArtist[row.artist_id]}
          totalVoters={totalMemberCount}
          emptyVotersLabel={dimmed ? '0 votes' : undefined}
          dimmed={dimmed}
          onPress={setOpenArtist}
        />
      ))}
    </div>
  );

  return (
    <>
      {days.length > 1 && (
        <div className="mb-4 flex flex-wrap gap-2">
          <FilterChip
            selected={dayFilter === null}
            onClick={() => {
              setDayFilter(null);
              setLineupView('list');
            }}
            pill="lg"
            className="lineup-chip"
          >
            All days
          </FilterChip>
          {days.map((day) => (
            <FilterChip
              key={day}
              selected={dayFilter === day}
              onClick={() => setDayFilter(day)}
              pill="lg"
              className="lineup-chip"
            >
              {formatDayAbbrev(day)}
            </FilterChip>
          ))}
        </div>
      )}

      {canShowSchedule && (
        <LineupViewToggle
          view={activeLineupView}
          onChange={setLineupView}
          className="!mx-0 !mb-4"
        />
      )}

      {activeLineupView === 'schedule' ? (
        <LineupScheduleGrid
          lineup={lineup}
          day={scheduleDay}
          selectedKeys={selectedKeys}
          votersByArtist={votersByArtist}
          groupSize={totalMemberCount}
          readOnly
          onPress={setOpenArtist}
        />
      ) : (
        <>
          {voted.length === 0 ? (
            <p className="py-6 text-center text-sm text-ink/60">No picks yet.</p>
          ) : (
            renderRows(voted)
          )}

          {unvoted.length > 0 && (
            <div>
              {showUnvoted ? (
                renderRows(unvoted, { dimmed: true })
              ) : (
                <div className="unvoted-toggle">
                  <span className="unvoted-toggle__label">
                    {unvoted.length} more artist{unvoted.length !== 1 ? 's' : ''} with no votes yet
                  </span>
                  <Button variant="secondary" size="sm" onClick={() => setShowUnvoted(true)}>
                    Show all
                  </Button>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {showBlockedFootnote && (
        <p className="blocked-footnote">
          Blocked members are hidden from your view, not removed from the group
        </p>
      )}

      <ArtistVotersModal
        artist={openArtist}
        voters={openArtist ? votersByArtist[openArtist.artist_id] || [] : []}
        viewerId={viewerId}
        totalMemberCount={totalMemberCount}
        showBlockedFootnote={showBlockedFootnote}
        context="group"
        selected={
          openArtist
            ? selectedKeys.has(voteKey(openArtist.artist_id, openArtist.performance_day))
            : false
        }
        onToggleVote={viewerId && eventId ? toggleVote : undefined}
        onClose={() => setOpenArtist(null)}
      />
    </>
  );
}
